import { useState } from "react";
import { ApiError, type InstanceAutoLock } from "@keydials/api-client";
import { Icon } from "@keydials/dial-workspace";
import { api, getAccessToken } from "../auth/AuthContext";

/** The choices a dial admin actually wants, rather than a free-text seconds box on a tablet. */
const TIMEOUT_CHOICES = [60, 120, 300, 600, 1800] as const;

function label(seconds: number): string {
  return seconds < 60 ? `${seconds} seconds` : seconds === 60 ? "1 minute" : `${Math.round(seconds / 60)} minutes`;
}

/**
 * Shared-device (kiosk) settings for one dial (KEY-268), shown to dial admins only and only where the
 * definition offers the mode at all.
 *
 * Off by default, and deliberately per-instance: the same care-home definition is typically allocated both
 * to a tablet on a corridor wall AND to the manager's desktop, and locking the manager out every five
 * minutes would be worse than not offering the feature.
 */
export function AutoLockPanel({
  instanceId,
  autoLock,
  onChanged,
}: {
  instanceId: string;
  autoLock: InstanceAutoLock;
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(enabled: boolean, timeoutSecondsOverride: number | null) {
    const token = getAccessToken();
    if (!token) return;

    setBusy(true);
    setError(null);
    try {
      await api.setInstanceAutoLock(token, instanceId, enabled, timeoutSecondsOverride);
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not update the shared-device setting.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kd-card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="kd-label-overline" style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Icon name="lock" size={13} />
        Shared device
      </div>

      <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 13.5, cursor: busy ? "default" : "pointer" }}>
        <input
          type="checkbox"
          checked={autoLock.enabled}
          disabled={busy}
          onChange={(e) => void save(e.target.checked, autoLock.timeoutSecondsOverride)}
          style={{ marginTop: 2 }}
        />
        <span>
          Lock this dial when it's left idle
          <span style={{ display: "block", color: "var(--text-muted)", fontSize: 12.5, marginTop: 2, lineHeight: 1.45 }}>
            For a tablet several people share. The screen drops to the dial's logo, and getting back in needs
            a name and a PIN — which returns to this dial only.
          </span>
        </span>
      </label>

      {autoLock.enabled && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5 }}>
          <span style={{ color: "var(--text-muted)" }}>Lock after</span>
          <select
            value={autoLock.timeoutSecondsOverride ?? ""}
            disabled={busy}
            onChange={(e) => void save(true, e.target.value === "" ? null : Number(e.target.value))}
            style={{ font: "inherit", fontSize: 13, padding: "4px 8px", borderRadius: 6, border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)" }}
          >
            <option value="">Default ({label(autoLock.definitionTimeoutSeconds)})</option>
            {TIMEOUT_CHOICES.map((seconds) => (
              <option key={seconds} value={seconds}>{label(seconds)}</option>
            ))}
          </select>
        </label>
      )}

      {error && <div role="alert" style={{ color: "var(--st-overdue)", fontSize: 12.5 }}>{error}</div>}
    </div>
  );
}
